import * as React from 'react';
import { Sparkles } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import { Callout, WorkspaceButton } from '@/components/ui/workspace';
import type { VendorReport } from '@/lib/api/vendor-intelligence-report';
import { formatDate } from '@/lib/utils/format';
import { byLine, type PrepareState } from './report-views';

/**
 * The optional focus line for the next Prepare: one input and the button
 * that sends it, shown only when asked for.
 */
export function FocusRow({
  prepare,
  action,
  onCancel,
}: {
  prepare: PrepareState;
  /** The button's words, for example "Prepare report". */
  action: string;
  onCancel: () => void;
}) {
  const disabled = prepare.pending || prepare.blocker !== null;
  return (
    <div className="flex w-full flex-wrap items-center gap-2" data-testid="vi-report-focus-row">
      <label htmlFor="vi-report-focus" className="sr-only">
        Focus for the report (optional)
      </label>
      <input
        id="vi-report-focus"
        autoFocus
        value={prepare.focus}
        onChange={(e) => prepare.setFocus(e.target.value)}
        disabled={prepare.pending}
        maxLength={500}
        placeholder="For example: Massachusetts statutory reporting"
        className="h-[30px] min-w-[200px] flex-1 rounded-card border border-ink-200 bg-paper-0 px-2.5 text-body-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-accent-amber-600 disabled:opacity-50"
      />
      <WorkspaceButton
        variant="primary"
        disabled={disabled}
        title={prepare.blocker?.message}
        onClick={prepare.run}
      >
        {prepare.pending ? <Spinner className="h-3.5 w-3.5" /> : null}
        {action}
      </WorkspaceButton>
      <WorkspaceButton variant="tertiary" disabled={prepare.pending} onClick={onCancel}>
        Cancel
      </WorkspaceButton>
    </div>
  );
}

/** The drafting notice, shown wherever Prepare was pressed. */
export function DraftingCallout() {
  return (
    <Callout tone="ai" icon={Sparkles} role="status">
      Drafting the report: one AI call per page and per shortlisted vendor. This takes a minute or
      two.
    </Callout>
  );
}

/**
 * The step before anything is published: one box. With a draft in
 * progress it names it and opens it; without one it offers Prepare
 * report, the button carrying its own reason while Prepare waits.
 */
export function EmptyReport({
  report,
  prepare,
  onOpenDraft,
}: {
  report: VendorReport;
  prepare: PrepareState;
  onOpenDraft: () => void;
}) {
  const [focusOpen, setFocusOpen] = React.useState(false);
  const draft = report.draft;
  const canEdit = report.can_edit;
  const blocker = prepare.blocker;
  return (
    <div
      data-testid="vi-report-empty"
      className="m-auto flex w-full max-w-[520px] flex-col items-center gap-3 rounded-card border border-dashed border-ink-200 bg-paper-0 px-6 py-7 text-center"
    >
      <StatusChip tone="ai" size="sm">
        AI drafts · you edit · code counts
      </StatusChip>
      {draft ? (
        <>
          <p className="m-0 text-body-sm text-ink-700">
            Draft · version {draft.version} is in progress, prepared{' '}
            {byLine(formatDate(draft.drafted_at), draft.drafted_by.name)}. Nothing is published yet.
          </p>
          {canEdit ? (
            <WorkspaceButton variant="primary" onClick={onOpenDraft}>
              Open draft
            </WorkspaceButton>
          ) : null}
        </>
      ) : (
        <>
          <p className="m-0 text-body-sm text-ink-700">
            {canEdit
              ? 'No report yet. Prepare report writes the pages from the confirmed ratings and the shortlist, with every claim cited and every number computed.'
              : 'No report yet. A consultant prepares it from the confirmed ratings.'}
          </p>
          {canEdit && !focusOpen ? (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <WorkspaceButton
                variant="primary"
                disabled={prepare.pending || blocker !== null}
                title={blocker?.message}
                onClick={prepare.run}
              >
                {prepare.pending ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Prepare report
              </WorkspaceButton>
              {blocker === null && !prepare.pending ? (
                <WorkspaceButton variant="tertiary" onClick={() => setFocusOpen(true)}>
                  Add a focus
                </WorkspaceButton>
              ) : null}
            </div>
          ) : null}
          {canEdit && focusOpen ? (
            <FocusRow
              prepare={prepare}
              action="Prepare report"
              onCancel={() => {
                prepare.setFocus('');
                setFocusOpen(false);
              }}
            />
          ) : null}
          {canEdit && blocker ? (
            <p
              data-testid={blocker.kind === 'shortlist' ? 'vi-report-shortlist-blocker' : undefined}
              className="m-0 text-meta text-ink-500"
            >
              {blocker.message}
            </p>
          ) : null}
        </>
      )}
      {prepare.pending ? <DraftingCallout /> : null}
      {prepare.error ? <ErrorMessage error={prepare.error} /> : null}
    </div>
  );
}
